import React from 'react'
import {
  Briefcase,
  CalendarDays,
  Clock,
  FolderKanban,
  Percent,
  Timer,
} from 'lucide-react'
import type { DisplaySummaryRow } from '../../../utils/projectOverviewSummary'

export interface ProjectOverviewKpiStripProps {
  displaySummary: DisplaySummaryRow
}

/** Same card / type scale as `RegionalKpiStrip`. */
export function ProjectOverviewKpiStrip({
  displaySummary,
}: ProjectOverviewKpiStripProps) {
  const th = Number(displaySummary.total_hours) || 0
  const bh = Number(displaySummary.billable_hours) || 0
  const nb = Number(displaySummary.non_billable_hours) || 0
  const lateRate = displaySummary.late_entry_rate
  const utilPct = th > 0 ? ((bh / th) * 100).toFixed(1) : '—'
  const leaveDays = displaySummary.total_leave_days

  const tiles = [
    {
      label: 'Total hours',
      value: th ? th.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—',
      sub: String(
        displaySummary.subtitle ||
          `${displaySummary.projects_count ?? 0} projects`,
      ),
      accent: 'bg-blue-500',
      textColor: 'text-blue-700',
      bg: 'bg-blue-50',
      icon: <Clock size={16} className="text-blue-500" />,
    },
    {
      label: 'Billable hours',
      value: bh ? bh.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—',
      sub: th > 0 ? `${((bh / th) * 100).toFixed(1)}% util` : '—',
      accent: 'bg-emerald-500',
      textColor: 'text-emerald-700',
      bg: 'bg-emerald-50',
      icon: <Timer size={16} className="text-emerald-500" />,
    },
    {
      label: 'Non-billable',
      value: nb ? nb.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '—',
      sub: 'Logged',
      accent: 'bg-amber-500',
      textColor: 'text-amber-700',
      bg: 'bg-amber-50',
      icon: <Briefcase size={16} className="text-amber-500" />,
    },
    {
      label: 'Projects',
      value: String(displaySummary.projects_count ?? 0),
      sub: String(displaySummary.subtitle || 'With timesheet data'),
      accent: 'bg-violet-500',
      textColor: 'text-violet-700',
      bg: 'bg-violet-50',
      icon: <FolderKanban size={16} className="text-violet-500" />,
    },
    {
      label: 'Late %',
      value: lateRate != null ? `${Number(lateRate).toFixed(1)}%` : '—',
      sub: 'Late / total entries',
      accent: 'bg-red-500',
      textColor: 'text-red-700',
      bg: 'bg-red-50',
      icon: <Percent size={16} className="text-red-500" />,
    },
    {
      label: 'Util %',
      value: utilPct === '—' ? '—' : `${utilPct}%`,
      sub: 'Billable share of total',
      accent: 'bg-teal-500',
      textColor: 'text-teal-700',
      bg: 'bg-teal-50',
      icon: <Percent size={16} className="text-teal-500" />,
    },
    {
      label: 'Leave days',
      value:
        leaveDays != null
          ? Number(leaveDays).toLocaleString(undefined, {
              maximumFractionDigits: 1,
            })
          : '—',
      sub: 'Total (full + half)',
      accent: 'bg-sky-500',
      textColor: 'text-sky-700',
      bg: 'bg-sky-50',
      icon: <CalendarDays size={16} className="text-sky-500" />,
    },
  ]

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-3">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="bg-white rounded-xl border border-slate-200 shadow-md p-4 flex gap-3 items-start"
        >
          <div
            className={`w-1 self-stretch rounded-full flex-none ${tile.accent}`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
              {tile.label}
            </p>
            <p
              className={`text-xl font-bold tabular-nums leading-tight ${tile.textColor}`}
            >
              {tile.value}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug line-clamp-2">
              {tile.sub}
            </p>
          </div>
          <div
            className={`w-8 h-8 rounded-lg ${tile.bg} flex items-center justify-center flex-none`}
          >
            {tile.icon}
          </div>
        </div>
      ))}
    </div>
  )
}
